import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { PlaceholderMedia } from "@/components/DeviceFrames";
import { useLang } from "@/i18n/translations";
import { PROJECTS, DEALALERT_ASSETS, HDH_ASSETS, NAP_ASSETS, NAA_ASSETS, NOVA_ASSETS, UH_ASSETS } from "@/lib/siteConfig";

// Real thumbnails for projects with delivered assets — centrally swappable.
const PROJECT_THUMBS = {
  dealalert: { src: DEALALERT_ASSETS.desktop.topdeals, alt: "DealAlert — Top Deals" },
  hausdeshandys: { src: HDH_ASSETS.desktop.hero, alt: "HausDesHandys — Premium Shop" },
  naperformance: { src: NAP_ASSETS.desktop.hero, alt: "NAPerformance — AI Performance Advisor" },
  naautomobile: { src: NAA_ASSETS.desktop.hero, alt: "NAAutomobile — Premium Digital Showroom" },
  novapilot: { src: NOVA_ASSETS.desktop.hero, alt: "NovaPilot — Digitaler Autoimport-Assistent" },
  unfallheld: { src: UH_ASSETS.desktop.hero, alt: "UnfallHeld — Digitaler Unfallassistent" },
};

const CardMedia = ({ project }) => {
  const thumb = project.hasAssets ? PROJECT_THUMBS[project.id] : null;
  if (thumb) {
    return (
      <div className="relative overflow-hidden rounded-lg border border-white/10" style={{ aspectRatio: "16/9.5" }}>
        <img
          src={thumb.src}
          alt={thumb.alt}
          loading="lazy"
          className="w-full h-full object-cover object-top block transition-transform duration-700 ease-out group-hover:scale-[1.03]"
        />
      </div>
    );
  }
  return <PlaceholderMedia aspect="16/9.5" testId={`project-card-placeholder-${project.id}`} />;
};

export const ProjectWall = () => {
  const { t } = useLang();

  return (
    <section id="referenzen" data-testid="project-wall" className="relative py-20 sm:py-28 bg-[#05070d]">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
        <SectionHeading kicker={t.wall.kicker} title={t.wall.title} subtitle={t.wall.subtitle} testId="project-wall-heading" />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {PROJECTS.map((p, idx) => (
            <Reveal key={p.id} delay={Math.min(idx * 0.08, 0.4)}>
              <article
                data-testid={`project-card-${p.id}`}
                className="group h-full flex flex-col rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[rgba(167,243,255,0.25)] hover:bg-white/[0.05] transition-[border-color,background-color] duration-300 p-5"
              >
                <Link
                  data-testid={`project-card-media-link-${p.id}`}
                  to={`/references/${p.id}`}
                  aria-label={`${p.name} — ${t.wall.caseStudy}`}
                  className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
                >
                  <CardMedia project={p} />
                </Link>
                <div className="mt-5 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-mono-cs uppercase tracking-[0.18em] text-[#00d4ff]/80">{t.categories[p.categoryKey]}</p>
                    <h3 className="mt-1.5 font-display text-xl font-semibold text-foreground">{p.name}</h3>
                  </div>
                  <a
                    data-testid={`project-live-link-${p.id}`}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${p.name} — ${t.wall.visit}`}
                    className="shrink-0 p-2 rounded-full border border-white/10 text-muted-foreground hover:text-[#00d4ff] hover:border-[#00d4ff]/40 transition-[color,border-color] duration-200"
                  >
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                </div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">{t.projects[p.id].claim}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {p.badges.map((b) => (
                    <Badge
                      key={b}
                      variant="outline"
                      className="bg-white/5 border-white/10 text-foreground/80 font-mono-cs text-[10px] font-normal rounded-full px-2.5"
                    >
                      {b}
                    </Badge>
                  ))}
                </div>
                <Link
                  data-testid={`project-case-link-${p.id}`}
                  to={`/references/${p.id}`}
                  className="mt-5 inline-flex items-center justify-center gap-2 self-start rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 px-4 h-9 text-xs font-mono-cs uppercase tracking-[0.12em] text-[#00d4ff] hover:bg-[#00d4ff]/12 hover:border-[#00d4ff]/55 transition-[background-color,border-color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {p.hasAssets ? t.wall.caseStudy : t.wall.comingSoon}
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
